import shopQueries from '@apis/queries/shopQueries';
import useCurrency from '@hooks/item/useCurrency';
import { OrderEntryPath } from '@models/order';
import orderWithTotalAmount from '@providers/OrderProvider/utils/orderWithTotalAmount';
import { RootState } from '@store/store';
import { useQuery } from '@tanstack/react-query';
import { orderLineToGtmFormat, pushEcommerceEvent } from '@utils/gtm';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

const CHECKOUT_SOURCES: Record<OrderEntryPath, string> = {
    cart: 'CART',
    direct: 'DIRECT',
    recent: 'RECENT_ORDER',
    reorder: 'REORDER',
};

interface Props {
    path?: OrderEntryPath;
    itemActivityId?: string;
}

/**
 * 주문서 진입 시 begin_checkout 이벤트를 1회 발화한다.
 * 상품 상세·장바구니는 주문 상태에 복사 전 이전 주문이 남아있을 수 있어, 복사 원본 요청을 기준으로 한다.
 * 최근 주문·재주문은 진입 전에 주문 상태로 복사되므로 주문 상태를 기준으로 한다.
 * path 가 없는 경우(토스 결제 성공·실패 후 재진입)는 발화하지 않는다.
 */
function useBeginCheckout({ path, itemActivityId }: Props) {
    const sourceRequest = useSelector((root: RootState) => {
        if (path === 'direct') {
            return itemActivityId ? root.item[itemActivityId] : undefined;
        }
        if (path === 'cart') {
            return root.cart;
        }
        if (path === 'recent' || path === 'reorder') {
            return root.order;
        }
        return undefined;
    });
    const shopId = sourceRequest?.shopId;
    const { data: shop } = useQuery({
        ...shopQueries.detail(shopId as string),
        enabled: !!shopId,
    });
    const currency = useCurrency();
    const isSent = useRef(false);

    useEffect(() => {
        if (isSent.current || !sourceRequest || !shop) return;
        const { orderLines, totalAmount } = orderWithTotalAmount(sourceRequest);
        if (orderLines.length === 0) return;
        isSent.current = true;
        pushEcommerceEvent('begin_checkout', {
            value: totalAmount,
            currency,
            checkout_source: path && CHECKOUT_SOURCES[path],
            items: orderLines
                .filter((orderLine) => orderLine.status === 'ON_SALE')
                .map((orderLine) => orderLineToGtmFormat({ orderLine, shop })),
        });
    }, [sourceRequest, shop, currency, path]);
}

export default useBeginCheckout;
